/**
 * Lecture du refus renvoye par S3 sur un PUT presigne — UPLOAD-ERR-001.
 *
 * POURQUOI CE MODULE EXISTE. Un coach (GFCA, 06/08/2026) a recu « Upload S3
 * echoue (400) » sur son premier envoi de match. S3 avait pourtant repondu un
 * document <Error> nommant la cause exacte dans <Code> — le navigateur l'a
 * recu, et les deux chemins d'upload l'ont jete sans le lire : `xhr.onload` ne
 * retenait que `xhr.status`, et `uploadService` ecrasait `error.response.data`
 * par une Error generique. L'origine du refus etait definitivement perdue au
 * moment ou elle devenait interessante.
 *
 * Un 400 S3 recouvre des causes sans rapport entre elles · signature expiree,
 * fichier au-dela de 5 Go, Content-Type qui ne correspond pas a celui signe,
 * cle refusee. Le code HTTP seul ne permet d'en ecarter aucune. Le <Code> les
 * separe en un mot.
 *
 * DOMParser, PAS DE REGEX. Le corps est du XML et peut arriver tronque,
 * vide, ou en HTML si un intermediaire reseau s'interpose. Un parseur qui
 * connait la grammaire echoue proprement la ou une regex renverrait une
 * capture fausse. DOMParser est natif : aucune dependance ajoutee.
 */

/**
 * Extrait { code, message, requestId } d'un corps de reponse S3.
 *
 * NE LEVE JAMAIS. Cette fonction n'est appelee que depuis un chemin d'erreur
 * deja degrade : une exception ici masquerait le defaut qu'on cherche a
 * rendre visible. Toute entree illisible — vide, nulle, non-XML, XML sans
 * <Error> — rend les trois champs a null.
 *
 * @param {string} xmlText corps brut de la reponse S3
 * @returns {{code: string|null, message: string|null, requestId: string|null}}
 */
export function parseS3Error(xmlText) {
  const vide = { code: null, message: null, requestId: null }

  if (typeof xmlText !== 'string' || !xmlText.trim()) return vide

  try {
    const doc = new DOMParser().parseFromString(xmlText, 'application/xml')

    // DOMParser ne leve pas sur un document invalide : il rend un document
    // portant un <parsererror>. C'est la seule facon de detecter l'echec.
    if (doc.querySelector('parsererror')) return vide

    const lire = (balise) => {
      const el = doc.querySelector(balise)
      const txt = el?.textContent?.trim()
      return txt || null
    }

    return {
      code:      lire('Code'),
      message:   lire('Message'),
      requestId: lire('RequestId'),
    }
  } catch {
    // Environnement sans DOMParser (SSR, harnais node). On ne casse pas le
    // chemin d'erreur pour autant.
    return vide
  }
}

/**
 * Traduit un <Code> S3 en une phrase destinee au coach.
 *
 * Le coach ne peut rien faire d'un `SignatureDoesNotMatch`. Il peut agir sur
 * « recharge la page et reessaie ». Le code brut n'est pas perdu pour autant :
 * il part dans Sentry et reste attache a l'objet Error.
 *
 * @param {string|null} code   valeur de <Code>, null si le corps etait illisible
 * @param {number|string} httpStatus statut HTTP, seul repli quand le code manque
 * @returns {string}
 */
export function s3ErrorLabel(code, httpStatus) {
  const LIBELLES = {
    EntityTooLarge:        'Fichier trop volumineux pour un envoi en une seule fois.',
    RequestTimeout:        "La connexion s'est interrompue pendant l'envoi. Réessaie.",
    SignatureDoesNotMatch: "Lien d'envoi invalide. Recharge la page et réessaie.",
    AccessDenied:          'Accès au stockage refusé. Contacte le support.',
    InvalidArgument:       'Fichier refusé par le stockage.',
  }

  if (!code) return `Envoi refusé par le stockage (HTTP ${httpStatus}).`
  return LIBELLES[code] || `Envoi refusé par le stockage (${code}).`
}
